import { useCallback, useMemo, useState, type ReactElement } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import SpeedIcon from '@mui/icons-material/Speed'
import WarningIcon from '@mui/icons-material/Warning'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import useResultsSectionOpen from '@editor/pages/Results2/hooks/useResultsSectionOpen'
import { useMutation } from '@tanstack/react-query'
import { EmptyState } from '@ui2/feedback'
import { MaterialSymbol } from '@ui2/icons'
import { Section } from '@ui2/structure'
import { UtilizationC90TextIcon, UtilizationTextIcon } from '@ui/icons'
import { enqueueSnackbar } from '@ui/snackbar'
import { useModelStore, useResultsStore, useTransmissionStore } from '@editorStores'
import { useElementLabel } from '@editorHooks'
import { downLoadFile } from '@utils'
import { getMemberChecksDocument } from '@mutations'
import type { DesignFormRootValues, MemberCheckRow } from '../types'
import {
  buildMemberCheckRows,
  buildMemberCheckSettingReferenceData,
  buildMemberCheckSettingSupportsContext,
  fmtUtilization,
  formatMemberCheckPosition,
  getMemberCheckSpecificSettingsKey,
  isMemberCheckRowHighlighted,
  isSupportCompressionMemberCheckType,
  isTimberLikeSettings,
  isVibrationError,
  memberCheckSpecificSettingsMatch,
  toggleSelectedCheckHighlight,
  toggleSelectedSupportHighlight,
} from '../utils'
import MemberCheckDetailContent from './MemberCheckDetailContent'
import MemberCheckSummary from './MemberCheckSummary'
import UtilisationRow from './UtilisationRow'

export const MemberChecksSection = ({
  elementGuid,
  disableForm = false,
}: {
  activeSettingsTab: string | null
  elementGuid?: string
  disableForm?: boolean
}): ReactElement => {
  const theme = useTheme()
  const { projectId } = useParams()
  const getLabel = useElementLabel()
  const project = useModelStore(state => state.project)
  const { getValues, setValue } = useFormContext<DesignFormRootValues>()
  const highlightedCheck = useResultsStore(state => state.highlightedCheck)
  const setHighlightedCheck = useResultsStore(state => state.setHighlightedCheck)
  const checksSection = useResultsSectionOpen('results2.design.checks')

  const handleRowClick = useCallback(
    (row: MemberCheckRow) => {
      if (!elementGuid || !('relative_position' in row.check)) return
      // Support compression checks highlight their `A`-label; every other
      // position check is marked at its position. Non-support checks are keyed
      // by check type too, since several can share a position.
      if (isSupportCompressionMemberCheckType(row.checkType)) {
        toggleSelectedSupportHighlight(
          elementGuid,
          row.check.relative_position,
          highlightedCheck,
          setHighlightedCheck,
        )
      } else {
        toggleSelectedCheckHighlight(
          elementGuid,
          row.check.relative_position,
          row.checkType,
          highlightedCheck,
          setHighlightedCheck,
        )
      }
    },
    [elementGuid, highlightedCheck, setHighlightedCheck],
  )

  const { mutate: downloadExportDocument, isLoading: isDownloadingExportDocument } = useMutation(
    async (element: string) => {
      const data: Blob = await getMemberChecksDocument.request(projectId as string, element)
      return {
        data,
        filename: `Bauteilnachweise ${getLabel(element)} - ${project.name || projectId}.docx`,
      }
    },
    {
      onSuccess: async ({ data, filename }) => {
        if (!data) {
          enqueueSnackbar('Daten noch nicht vorhanden', { variant: 'warning' })
          return
        }

        downLoadFile(
          data,
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          filename,
        )
      },
      onError: () => {
        enqueueSnackbar('Fehler beim Abrufen des Dokuments', { variant: 'error' })
      },
    },
  )
  const checksFieldPath = 'elements.0.checksSelectedElement'
  const checks = useWatch({
    name: checksFieldPath,
  }) as CombinedPositionCheck[] | undefined
  const memberSettingsFieldPath = 'elements.0.memberCheckSettingsSelectedElement'
  const memberSettings = useWatch({
    name: memberSettingsFieldPath,
  }) as SettingsOnMember | null | undefined
  const specificSettingsFieldPath = `${memberSettingsFieldPath}.check_specific_settings`
  const checkSpecificSettings = useWatch({
    name: specificSettingsFieldPath,
  }) as MemberCheckSpecificSetting[] | undefined
  const watchedElementGuid = useWatch({
    name: 'elements.0.elementGuid',
  }) as string | undefined
  const crossSection = useWatch({
    name: 'elements.0.crossSection',
  }) as CrossSectionTimberOrCLTOrSteel | null | undefined
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  // Saved tolerances only apply while cross section / support situation still
  // match the reference data they were saved with.
  const settingValidationContext = useMemo(
    () => ({
      crossSection,
      supports: buildMemberCheckSettingSupportsContext(
        watchedElementGuid,
        verticalTransmissionGraph,
      ),
    }),
    [crossSection, verticalTransmissionGraph, watchedElementGuid],
  )
  const rows = useMemo(
    () => buildMemberCheckRows(checks ?? [], checkSpecificSettings ?? [], settingValidationContext),
    [checkSpecificSettings, checks, settingValidationContext],
  )
  const [detailsRow, setDetailsRow] = useState<MemberCheckRow | null>(null)
  const [detailsAnchorEl, setDetailsAnchorEl] = useState<HTMLElement | null>(null)
  const topCheck = rows.length > 0 ? rows[0] : null
  const closeDetails = () => {
    setDetailsRow(null)
    setDetailsAnchorEl(null)
  }
  const updateCheckSpecificValue = (row: MemberCheckRow, value: string) => {
    const settings = getValues(
      memberSettingsFieldPath as 'elements.0.memberCheckSettingsSelectedElement',
    )
    if (!settings) return

    const key = getMemberCheckSpecificSettingsKey(row.check)
    const existingSettings = settings.check_specific_settings ?? []
    const withoutCurrent = existingSettings.filter(
      setting => !memberCheckSpecificSettingsMatch(setting, key),
    )

    if (value === '') {
      setValue(
        specificSettingsFieldPath as 'elements.0.memberCheckSettingsSelectedElement.check_specific_settings',
        withoutCurrent,
        { shouldDirty: true, shouldTouch: true },
      )
      return
    }

    const numericValue = Number(value.replace(',', '.'))
    if (!Number.isFinite(numericValue)) return

    setValue(
      specificSettingsFieldPath as 'elements.0.memberCheckSettingsSelectedElement.check_specific_settings',
      [
        ...withoutCurrent,
        {
          ...key,
          value: numericValue,
          // A tolerance only applies together with the design context it was
          // entered against, so record that context right away.
          reference_data: buildMemberCheckSettingReferenceData(
            key,
            settingValidationContext,
            isTimberLikeSettings(settings) ? settings.support_configs : [],
          ),
        },
      ],
      { shouldDirty: true, shouldTouch: true },
    )
  }
  return (
    <Section
      title="Nachweise"
      collapsible
      hideBorderBottom
      {...checksSection}
      summary={
        topCheck ? (
          <MemberCheckSummary
            label={topCheck.label}
            maxUtilization={topCheck.maxUtilization}
            checkType={topCheck.checkType}
            allowedOverutilizationPercent={topCheck.checkSpecificSetting?.value}
          />
        ) : null
      }
      actions={
        isDownloadingExportDocument ? (
          <Box
            sx={{
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={14} aria-label="Export wird erstellt" />
          </Box>
        ) : (
          <Tooltip title="Exportieren (docx)" arrow disableInteractive>
            <span>
              <IconButton
                size="small"
                disabled={disableForm || !elementGuid}
                onClick={() => elementGuid && downloadExportDocument(elementGuid)}
              >
                <MaterialSymbol icon="download" size={18} />
              </IconButton>
            </span>
          </Tooltip>
        )
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75, mt: 0.75 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 50px 50px 50px 8px 35px 28px',
            gap: 0.75,
            width: '100%',
            px: 0.75,
            fontSize: 12,
            color: theme.palette.text.secondary,

            textAlign: 'center',
          }}
        >
          <span />
          <Box component="span" sx={{ justifySelf: 'center' }}>
            <UtilizationTextIcon />
          </Box>
          <Box component="span" sx={{ justifySelf: 'center' }}>
            <UtilizationC90TextIcon />
          </Box>
          <span>pos.</span>
          <span />
          <Tooltip title="Akzeptierte Ausnutzung festlegen (nur für die Anzeige)">
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.25,
              }}
            >
              <SpeedIcon sx={{ fontSize: 16 }} />
              <WarningIcon sx={{ fontSize: 15 }} />
            </Box>
          </Tooltip>
          <span />
        </Box>
      </Box>
      {rows.length === 0 ? (
        <EmptyState>Keine Nachweise vorhanden</EmptyState>
      ) : (
        rows.map(row => {
          const isSelectableRow = 'relative_position' in row.check
          return (
            <UtilisationRow
              key={row.id}
              label={row.label}
              maxUtilization={row.maxUtilization}
              maxUtilizationC90={row.maxUtilizationC90}
              position={row.position}
              checkType={row.checkType}
              highlighted={isMemberCheckRowHighlighted(elementGuid, highlightedCheck, row)}
              // hidden for now
              // settingsTabRelevant={isCheckRelevantForSettingsTab(row.checkType, activeSettingsTab)}
              onRowClick={isSelectableRow ? () => handleRowClick(row) : undefined}
              onInfoClick={e => {
                setDetailsRow(row)
                setDetailsAnchorEl(e.currentTarget)
              }}
              numberValue={
                row.checkSpecificSetting?.value == null
                  ? ''
                  : String(row.checkSpecificSetting.value)
              }
              allowedOverutilizationPercent={row.checkSpecificSetting?.value}
              disabled={disableForm || !memberSettings}
              onNumberChange={value => updateCheckSpecificValue(row, value)}
            />
          )
        })
      )}

      <Popover
        open={Boolean(detailsRow && detailsAnchorEl)}
        anchorEl={detailsAnchorEl}
        onClose={closeDetails}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 420,
            maxWidth: 'min(420px, calc(100vw - 24px))',
            borderRadius: 1.25,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
          },
          onClick: closeDetails,
        }}
      >
        <Box sx={{ px: 1.5, py: 1.25 }}>
          <Box
            sx={{
              fontFamily: 'Roboto',
              fontSize: 12.5,
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            {detailsRow?.label ?? 'Nachweis'}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 1,
              fontSize: 12,
              fontFamily: 'Roboto',
              color: theme.palette.text.secondary,
            }}
          >
            <Box>
              <Typography
                component="span"
                sx={{ fontSize: 11, fontFamily: 'Roboto', color: 'text.secondary' }}
              >
                <UtilizationTextIcon />
              </Typography>
              <Box sx={{ fontSize: 13, color: 'text.primary' }}>
                {detailsRow
                  ? isVibrationError(detailsRow.checkType, detailsRow.maxUtilization)
                    ? 'ERR'
                    : fmtUtilization(detailsRow.maxUtilization) || '—'
                  : '—'}
              </Box>
            </Box>
            <Box>
              <Typography
                component="span"
                sx={{ fontSize: 11, fontFamily: 'Roboto', color: 'text.secondary' }}
              >
                <UtilizationC90TextIcon />
              </Typography>
              <Box sx={{ fontSize: 13, color: 'text.primary' }}>
                {detailsRow ? fmtUtilization(detailsRow.maxUtilizationC90) || '—' : '—'}
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 11, fontFamily: 'Roboto', color: 'text.secondary' }}>
                pos.
              </Typography>
              <Box sx={{ fontSize: 13, color: 'text.primary' }}>
                {detailsRow ? formatMemberCheckPosition(detailsRow.position) : '—'}
              </Box>
            </Box>
          </Box>

          {detailsRow ? (
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <MemberCheckDetailContent check={detailsRow.check} />
            </Box>
          ) : null}

          <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              size="small"
              onClick={closeDetails}
              sx={{ textTransform: 'none', fontFamily: 'Roboto' }}
            >
              Schließen
            </Button>
          </Box>
        </Box>
      </Popover>
    </Section>
  )
}
